import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { parseISO } from 'date-fns';
import omit from 'lodash/omit';
import { Dispatch, useContext, useEffect, useState } from 'react';
import { Button, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import { useForm, useWatch } from 'react-hook-form';
import { toast } from 'react-toastify';
import {
  ApiError,
  BookingRequestRead,
  BookingRequestReadStatusEnum,
  BookingRequestsService,
  ConfirmBookingRequest,
  RespondBookingRequest,
  StatusByProviderEnum,
  StatusByRepEnum,
  UpdateBookingRequestByStaff,
  UserTypeEnum,
} from '../../../api';

import { generateShowFieldErrors } from '../../../utils/forms';
import { FormSubmitContext } from '../../contexts/FormSubmitContext';
import { SessionContext } from '../../contexts/SessionContext';

import { bookingRequestsQueryKeys } from '../../query-keys-factories/booking-requests';
import { userQueryKeys } from '../../query-keys-factories/user';
import { handleApiError } from '../../utils/errorhandling';
import { FacilityLogo } from '../FacilityLogo';
import { FacilityRepresentation } from '../FacilityRepresentation/FacilityRepresentation';
import { FormFeedback } from '../FormFeedback';
import { HealthcareEntityRepRepresentation } from '../HealthcareEntityRepRepresentation';
import { ProviderRepresentation } from '../ProviderRepresentation';
import { ServerErrorsAlert } from '../ServerErrorsAlert';
import { ShiftDocumentArray } from '../ShiftDocumentArray';
import { BookingRequestHistory } from './BookingRequestHistory';
import { BookingTable } from './BookingTable';
import { DialogFooter } from './DialogFooter';
import { DialogHeader } from './DialogHeader';
import { ExtraIncomeForms } from './ExtraIncomeForms';
import { ExtraIncomeProvider } from './ExtraIncomeProvider';
import './BookingRequestDialog.scss';
import {
  confirmAction,
  convertBookingRequestFormToApi,
  getAllowShiftEdit,
  getSubmitAction,
  respondAction,
  updateAction,
  getAllowUpdates,
  getAllBookingsInPast,
  getAllowCancel,
} from './utils';
import { providerValidationSchema, repValidationSchema } from './validation-schemas';

interface BookingRequestDialogProps {
  bookingRequest: BookingRequestRead;
  show: boolean;
  showConfirmationModal: () => Promise<boolean>;
  onHide?: () => void;
  refetch: () => void;
  isLoading?: boolean;
  onUpdateStatus?: () => void | undefined;
  setShowFullDecline: Dispatch<boolean>;
  showFullDecline: boolean;
}

export const BookingRequestDialog = ({
  bookingRequest,
  show,
  showConfirmationModal,
  onHide: externalOnHide,
  refetch,
  isLoading = false,
  onUpdateStatus,
  setShowFullDecline,
  showFullDecline,
}: BookingRequestDialogProps) => {
  const {
    bookings,
    provider,
    healthcare_entity_rep,
    healthcare_facility,
    overall_total,
    total_hours,
  } = bookingRequest;
  const { user } = useContext(SessionContext);
  const { isSubmitEnabled } = useContext(FormSubmitContext);
  const userIsProvider = user?.user_type == UserTypeEnum.PROVIDER;
  const [showGlobalServerErrors, setShowGlobalServerErrors] = useState(false);
  const [serverErrors, setServerErrors] = useState<ApiError>();

  const [showServerFieldErrors, setShowServerFieldErrors] = useState<{
    comment?: boolean;
    bookings?: {
      [key: string]: {
        id?: boolean;
        status_by_provider?: boolean;
        status_by_rep?: boolean;
      };
    };
  }>({});
  const {
    clearErrors,
    handleSubmit,
    register,
    reset,
    setValue,
    control,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(userIsProvider ? providerValidationSchema : repValidationSchema),
  });

  const thirtyDaysFromNow = new Date();
  thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);

  const bookingsWatch = useWatch({ control: control, name: 'bookings' });

  const allowUpdates = getAllowUpdates(bookingRequest, userIsProvider);
  const allBookingsInPast = getAllBookingsInPast(bookings);
  const allowCancel = getAllowCancel(bookings, bookingRequest.status);

  useEffect(() => {
    if (!bookings?.length) return;

    // Set initial form values and check initial state
    const initialFormValues = bookings.map((booking) => ({
      id: booking.id,
      [userIsProvider ? 'status_by_provider' : 'status_by_rep']:
        booking.status_by_provider !== StatusByProviderEnum.REJECTED &&
        booking.status_by_rep !== StatusByRepEnum.REJECTED,
    }));

    reset({
      bookings: initialFormValues,
      comment: '',
    });

    const allUnchecked = initialFormValues.every((booking) =>
      userIsProvider ? !booking['status_by_provider'] : !booking['status_by_rep']
    );

    setShowFullDecline(allUnchecked);
  }, [bookings, setShowFullDecline, userIsProvider, reset]);

  // Keep the watch effect for subsequent changes
  useEffect(() => {
    if (!bookingsWatch) return;

    const allUnchecked = bookingsWatch.every((booking) => {
      if (userIsProvider) {
        return !booking.status_by_provider;
      }
      return !booking.status_by_rep;
    });

    setShowFullDecline(allUnchecked);
  }, [bookingsWatch, setShowFullDecline, userIsProvider]);

  const submitAction = getSubmitAction(bookingRequest, userIsProvider);

  const queryClient = useQueryClient();
  const mutationBookingUpdate = useMutation({
    mutationFn: async (data: UpdateBookingRequestByStaff) => {
      switch (submitAction) {
        case respondAction:
          return await BookingRequestsService.bookingRequestsRespondUpdate({
            requestId: bookingRequest.id,
            requestBody: data as RespondBookingRequest,
          });

        case updateAction:
          return await BookingRequestsService.bookingRequestsUpdate({
            requestId: bookingRequest.id,
            requestBody: data,
          });

        case confirmAction:
          return await BookingRequestsService.bookingRequestsConfirmUpdate({
            requestId: bookingRequest.id,
            requestBody: data as ConfirmBookingRequest,
          });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(bookingRequestsQueryKeys.allLists());
      queryClient.invalidateQueries(bookingRequestsQueryKeys.allDetails());
      queryClient.invalidateQueries(userQueryKeys.notifications());
      onUpdateStatus && onUpdateStatus();
    },
  });

  function handleClearErrorsAlert() {
    setShowGlobalServerErrors(false);
  }

  const handleSubmitClick = () => {
    clearErrors();
    setShowGlobalServerErrors(false);
    setShowServerFieldErrors({});
  };

  const handleClickApplyToAllBookings = (status) => {
    const statusBoolean =
      status === StatusByProviderEnum.ACCEPTED || status === StatusByRepEnum.ACCEPTED;

    if (userIsProvider) {
      bookings.forEach((booking, index) => {
        if (parseISO(booking.start_on) < new Date()) return;
        setValue(`bookings.${index}.status_by_provider`, statusBoolean, {
          shouldValidate: true,
          shouldDirty: true,
          shouldTouch: true,
        });
      });
    } else {
      bookings.forEach((booking, index) => {
        if (
          parseISO(booking.start_on) < new Date() ||
          booking.status_by_provider === StatusByProviderEnum.REJECTED
        )
          return;
        setValue(`bookings.${index}.status_by_rep`, statusBoolean, {
          shouldValidate: true,
          shouldDirty: true,
          shouldTouch: true,
        });
      });
    }
  };

  const onHide = () => {
    externalOnHide?.();
  };

  const onSubmit = async (data) => {
    if (userIsProvider) {
      const accepted = await showConfirmationModal();
      if (!accepted) {
        return;
      }
    }

    const adjustedData = convertBookingRequestFormToApi(userIsProvider, data);

    try {
      await mutationBookingUpdate.mutateAsync({ ...bookingRequest, ...adjustedData });
      toast.success('Assignment Request updated successfully');
      onHide();
    } catch (errors) {
      if (errors.status === 400) {
        const fieldErrors = omit(errors.body, ['non_field_errors']);
        setServerErrors(errors);
        setShowServerFieldErrors(generateShowFieldErrors(fieldErrors));
        setShowGlobalServerErrors(true);
      }
      handleApiError(errors);
    }
  };

  return (
    <Form noValidate onSubmit={handleSubmit(onSubmit)}>
      <Modal show={show} dialogClassName="container booking-request-dialog" onHide={onHide}>
        {isLoading ? (
          <Row key="loading-row">
            <Col xs={12} className="d-flex align-items-center justify-content-center">
              <Spinner
                aria-label="loading indicator"
                animation="grow"
                className="loading-spinner"
                variant="primary"
              />
            </Col>
          </Row>
        ) : (
          <div key="content-container">
            <DialogHeader bookingRequest={bookingRequest} />
            <ExtraIncomeProvider requestId={bookingRequest.id}>
              <Modal.Body
                className="py-0"
                style={{ maxHeight: 'calc(85vh - 220px)', overflow: 'auto' }}
              >
                <Row>
                  <Col>
                    <ServerErrorsAlert
                      errors={serverErrors}
                      className="m-3"
                      showErrors={showGlobalServerErrors}
                      clearErrors={handleClearErrorsAlert}
                    />
                  </Col>
                </Row>
                <Row className="align-items-stretch main-content">
                  <Col xs={12} lg={6} className="">
                    <div className="h-100 pt-2">
                      <div className="d-flex">
                        <h5 className="flex-grow-1 ps-2 text-section-name--dark-brand">
                          {bookingRequest.status === BookingRequestReadStatusEnum.PENDING
                            ? 'Proposed Shifts'
                            : 'Shifts'}
                        </h5>
                        {!allBookingsInPast && submitAction && allowUpdates && (
                          <div className="d-flex">
                            <Button
                              variant="link"
                              className="text-danger pt-0"
                              onClick={() =>
                                handleClickApplyToAllBookings(StatusByProviderEnum.REJECTED)
                              }
                            >
                              Decline all
                            </Button>
                            <Button
                              variant="link"
                              className="pt-0"
                              onClick={() =>
                                handleClickApplyToAllBookings(StatusByProviderEnum.ACCEPTED)
                              }
                            >
                              Accept all
                            </Button>
                          </div>
                        )}
                      </div>
                      <div className="table-responsive position-relative">
                        <BookingTable
                          bookings={bookings}
                          bookingRequest={bookingRequest}
                          userIsProvider={userIsProvider}
                          allowShiftEdit={getAllowShiftEdit}
                          register={register}
                          setValue={setValue}
                        />
                      </div>
                    </div>
                  </Col>
                  <Col xs={12} lg={6} className="d-flex flex-column">
                    <div className="py-2 mb-3">
                      <div>
                        <h5 className="text-section-name--dark-brand">Facility</h5>
                        <div className="d-flex gap-2 align-items-center">
                          <FacilityLogo className="flex-grow-0 flex-shrink-0" size={68} />
                          <FacilityRepresentation
                            facility={healthcare_facility}
                            className="flex-grow-1"
                            sentBy={!userIsProvider ? healthcare_entity_rep : undefined}
                          />
                        </div>
                      </div>
                      {userIsProvider ? (
                        <div className="mt-2">
                          <small>Sent by</small>
                          <HealthcareEntityRepRepresentation
                            healthcareEntityRep={healthcare_entity_rep}
                          />
                        </div>
                      ) : (
                        <div className="mt-2">
                          <small>Provider</small>
                          <ProviderRepresentation provider={provider} />
                        </div>
                      )}
                    </div>
                    <div className="flex-grow-1">
                      <div className="d-flex flex-column h-100 pb-3">
                        {allowUpdates && (
                          <Form.Group controlId="comment" className="d-flex flex-column h-100">
                            <Form.Control
                              type="text"
                              placeholder={
                                'Write a comment to the other party explaining your decision. They will be able to see this comment.'
                              }
                              as="textarea"
                              {...register('comment', {
                                onChange: () =>
                                  setShowServerFieldErrors({
                                    ...omit(showServerFieldErrors, 'comment'),
                                  }),
                              })}
                              className="flex-grow-1"
                              disabled={allBookingsInPast || !submitAction}
                              style={{
                                boxSizing: 'border-box',
                                height: '100%',
                                minHeight: '100px',
                              }}
                              isInvalid={!!errors?.comment || !!showServerFieldErrors?.comment}
                            />
                            <FormFeedback
                              clientError={errors?.comment}
                              serverErrors={serverErrors?.body?.comment ?? null}
                            />
                          </Form.Group>
                        )}
                      </div>
                    </div>
                  </Col>
                </Row>
                <Row className="flex-wrap">
                  <Col xs={12} lg={6} className="mb-3 d-flex">
                    {!userIsProvider &&
                    bookingRequest.status !== BookingRequestReadStatusEnum.CANCELED ? (
                      <ExtraIncomeForms refetch={refetch} className="flex-grow-1" />
                    ) : null}
                  </Col>
                  <Col xs={12} lg={6} className="mb-3">
                    <div className="h-100">
                      <ShiftDocumentArray className="border-top" requestId={bookingRequest.id} />
                    </div>
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col>
                    <div className="p-1">
                      <BookingRequestHistory bookingRequestId={bookingRequest.id} />
                    </div>
                  </Col>
                </Row>
              </Modal.Body>
              <DialogFooter
                bookings={bookings}
                totalValue={overall_total}
                overallBooking={bookingRequest.overall_booking}
                totalHours={total_hours}
                userIsProvider={userIsProvider}
                allowCancel={allowCancel}
                bookingRequestId={bookingRequest.id}
                refetch={refetch}
                queryClient={queryClient}
                onUpdateStatus={onUpdateStatus}
                externalOnHide={externalOnHide}
                allBookingsInPast={allBookingsInPast}
                allowUpdates={allowUpdates}
                submitAction={submitAction}
                handleSubmit={handleSubmit}
                onSubmit={onSubmit}
                handleSubmitClick={handleSubmitClick}
                mutationBookingUpdate={mutationBookingUpdate}
                isSubmitEnabled={isSubmitEnabled}
                showFullDecline={showFullDecline}
              />
            </ExtraIncomeProvider>
          </div>
        )}
      </Modal>
    </Form>
  );
};
